import { useQuery } from "@tanstack/react-query";
import ApiAdmin, { IPermissionType, IFolderPermission } from "../api/admin";
import { IComputedFolderPermission } from "../api/admin";

export interface Folder {
  id: number;
  label: string;
  user_ids: number[];
  group_ids: number[];
  organization_order_type_ids: number[];
  deleted_at?: string | null;
}

export interface FolderPermissions {
  userPermissions: Map<number, number>;
  groupPermissions: Map<number, number>;
  selectedUserIds: number[];
  selectedGroupIds: number[];
}

export interface PermissionTypes {
  permission_types: IPermissionType[];
}

export type { IComputedFolderPermission as ComputedFolderPermission } from "../api/admin";

export interface FolderPermissionsData {
  permissions: IFolderPermission[];
  user_permissions: IComputedFolderPermission[];
}

export interface UsersData {
  users: Array<{
    id: number;
    name: string;
    email: string;
  }>;
}

export interface GroupsData {
  groups: Array<{
    id: number;
    name: string;
    user_ids: number[];
  }>;
}

export function useFolderPermissions(
  organizationId: number,
  includeDeactivated: boolean = false,
  fetchUsersAndGroups: boolean = false
) {
  const foldersQuery = useQuery({
    queryKey: ["folders", organizationId, includeDeactivated],
    queryFn: () => ApiAdmin.getFolders(organizationId, includeDeactivated),
  });

  const usersQuery = useQuery({
    queryKey: ["users", organizationId],
    queryFn: () => ApiAdmin.getUsers(organizationId),
    enabled: fetchUsersAndGroups,
  });

  const groupsQuery = useQuery({
    queryKey: ["groups", organizationId],
    queryFn: () => ApiAdmin.getGroups(organizationId),
    enabled: fetchUsersAndGroups,
  });

  return {
    folders: foldersQuery.data?.folders || [],
    users: usersQuery.data?.users || [],
    groups: groupsQuery.data?.groups || [],
    isLoadingFolders: foldersQuery.isLoading,
    isLoadingUsers: usersQuery.isLoading,
    isLoadingGroups: groupsQuery.isLoading,
    foldersError: foldersQuery.error,
    usersError: usersQuery.error,
    groupsError: groupsQuery.error,
  };
}

export function useFolderPermissionDetails(
  organizationId: number,
  folderId: number | null
) {
  const permissionTypesQuery = useQuery({
    queryKey: ["permissionTypes", organizationId],
    queryFn: () => ApiAdmin.getPermissionTypes(organizationId),
  });

  const userPermissionsQuery = useQuery({
    queryKey: ["computedFolderPermissions", organizationId, folderId],
    queryFn: () =>
      folderId ? ApiAdmin.getFolderPermissions(organizationId, folderId) : null,
    enabled: folderId !== null,
    staleTime: 0,
    refetchOnMount: true,
  });

  return {
    permissionTypes: permissionTypesQuery.data?.permission_types || [],
    userPermissions: userPermissionsQuery.data?.user_permissions || [],
    permissions: userPermissionsQuery.data?.permissions || [],
    isLoadingPermissionTypes: permissionTypesQuery.isLoading,
    isLoadingUserPermissions: userPermissionsQuery.isLoading,
    permissionTypesError: permissionTypesQuery.error,
    userPermissionsError: userPermissionsQuery.error,
  };
}
